/**
 * TileDetailSections — per-type expanded detail components for TileDetailSheet.
 *
 * Split from TileDetailSheet.tsx to keep file sizes bounded (<400 lines each).
 * Each section renders the expanded content shown in the bottom sheet when a
 * tile is tapped in view mode (core-flows §4.1).
 *
 * Local utility widget components (note.sticky, todo.list, countdown.event)
 * live in LocalWidgetDetailSections.tsx — see that file for edit-in-place UX.
 *
 * Immutability: all components are pure — no prop/state mutation.
 */
import React, { useState, useEffect } from 'react';
import type { Tile } from 'briefpane/schema';
import type { GlanceViewModel, AnalyticsEmitter } from 'briefpane/contracts';
import type { CommitError } from '../board/use-editable-board.js';
import { SportsTeamPicker } from './SportsTeamPicker.js';
import type { CalendarEvent } from '../../adapters/calendar/types.js';
import {
  NoteDetail,
  TodoDetail,
  CountdownDetail,
} from './LocalWidgetDetailSections.js';

export interface DetailContentProps {
  readonly tile: Tile;
  readonly vm: GlanceViewModel;
  readonly data?: Record<string, unknown>;
  readonly onConfigure: (
    tileId: string,
    newParams: Record<string, unknown>,
    settingKey?: 'sports_team' | 'watchlist_symbols' | 'news_topic' | 'location' | 'other',
  ) => CommitError;
  readonly analytics: AnalyticsEmitter;
}

/* -------------------------------------------------------------------------- */
/* Clock formatting helper                                                      */
/* -------------------------------------------------------------------------- */

function formatTime(tz: string, format: string): string {
  return new Date().toLocaleTimeString('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: format === '12h',
  });
}

/* -------------------------------------------------------------------------- */
/* Per-type detail components                                                   */
/* -------------------------------------------------------------------------- */

export function WeatherDetail({
  vm,
  data,
}: {
  vm: GlanceViewModel;
  data?: Record<string, unknown>;
}): React.ReactElement {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
        <span style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1 }}>
          {vm.slots.primary}
        </span>
        {vm.slots.delta && (
          <span style={{ fontSize: 'var(--text-detail)', color: 'var(--color-text-secondary)' }}>
            {vm.slots.delta}
          </span>
        )}
      </div>
      {/* Enriched weather data when available from the cache (defensive — never crashes).
          Field names match WeatherData (normalize.ts): humidityPct, windMph, tempC. */}
      {data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', fontSize: 'var(--text-delta)', color: 'var(--color-text-secondary)' }}>
          {typeof data.humidityPct === 'number' && (
            <span>Humidity: {data.humidityPct}%</span>
          )}
          {typeof data.windMph === 'number' && (
            <span>Wind: {data.windMph} mph</span>
          )}
          {typeof data.tempC === 'number' && (
            <span>Temperature: {data.tempC}°C</span>
          )}
        </div>
      )}
      {!data && (
        <p style={{ fontSize: 'var(--text-delta)', color: 'var(--color-text-secondary)', margin: 0 }}>
          Tap "View full forecast" for hourly details, humidity, wind speed, and UV index.
        </p>
      )}
    </div>
  );
}

export function MarketsDetail({ tile, vm }: { tile: Tile; vm: GlanceViewModel }): React.ReactElement {
  const params = tile.source?.params as Record<string, unknown> | undefined;
  const symbols = params?.symbols as string[] | undefined;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {symbols && symbols.length > 0 && (
        <div style={{ fontSize: 'var(--text-label)', color: 'var(--color-text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {symbols.join(' · ')}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
        <span style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1 }}>
          {vm.slots.primary}
        </span>
        {vm.slots.delta && (
          <span style={{
            fontSize: 'var(--text-detail)',
            fontWeight: 500,
            color: vm.glanceState === 'positive' ? 'var(--color-positive-accent)' : vm.glanceState === 'negative' ? 'var(--color-negative-accent)' : 'var(--color-text-secondary)',
          }}>
            {vm.slots.delta}
          </span>
        )}
      </div>
      <p style={{ fontSize: 'var(--text-delta)', color: 'var(--color-text-secondary)', margin: 0 }}>
        Price and change reflect delayed data. Tap the link below for real-time quotes.
      </p>
    </div>
  );
}

export function NewsDetail({ tile, vm }: { tile: Tile; vm: GlanceViewModel }): React.ReactElement {
  const params = tile.source?.params as Record<string, unknown> | undefined;
  const activeQuery = typeof params?.['query'] === 'string' && params['query'] ? params['query'] : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <p style={{ fontSize: 'var(--text-detail)', fontWeight: 500, color: 'var(--color-text-primary)', margin: 0, lineHeight: 1.5 }}>
        {vm.slots.primary}
      </p>
      {vm.slots.delta && (
        <p style={{ fontSize: 'var(--text-delta)', color: 'var(--color-text-dim)', margin: 0 }}>
          {vm.slots.delta}
        </p>
      )}
      {activeQuery && (
        <p style={{ fontSize: 'var(--text-label)', color: 'var(--color-text-dim)', margin: 0 }}>
          Keyword: {activeQuery}
        </p>
      )}
    </div>
  );
}

/**
 * RssDetail — expanded view for rss.feed tiles.
 *
 * Shows the item list (title + relative time + safe https links), the feed hostname
 * as a source line, and honest error copy when the feed is blocked/invalid.
 *
 * PRIVACY note (ADR-005): feedUrl is a private user param. We show only the
 * hostname (not the full URL) in the detail sheet. feedUrl never appears in
 * any analytics event property.
 *
 * Link safety: only links validated as https with no credentials are rendered
 * as <a> elements (stored by fetch-feed.ts; all others are plain text).
 */
export function RssDetail({
  tile,
  vm,
  data,
}: {
  tile: Tile;
  vm: GlanceViewModel;
  data?: Record<string, unknown>;
}): React.ReactElement {
  const params = tile.source?.params as { feedUrl?: string } | undefined;
  const feedUrl = params?.feedUrl ?? '';
  // Show hostname only — full URL is private (ADR-005)
  let feedHost = '';
  try {
    feedHost = new URL(feedUrl).hostname;
  } catch {
    feedHost = feedUrl.slice(0, 40);
  }

  // Items are stashed under _items by use-tile-view-model (mirrors _events for calendar)
  type RssItemShape = { title: string; publishedAt: string; link?: string };
  const rawItems = (data as { _items?: unknown[] } | undefined)?.['_items'];
  const items: RssItemShape[] = Array.isArray(rawItems)
    ? (rawItems as RssItemShape[]).filter(
        (it): it is RssItemShape =>
          typeof it === 'object' && it !== null && typeof (it as RssItemShape).title === 'string',
      )
    : [];

  function formatRelativeTime(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60_000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const h = Math.floor(diffMin / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }

  // Error state: show honest message
  if (vm.dataState === 'error') {
    const isCors = vm.slots.delta === "Can't access";
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <p style={{ margin: 0, fontSize: 'var(--text-detail)', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
          {isCors
            ? "This feed doesn't allow direct access from browsers. Some feeds require server-side proxying."
            : vm.slots.delta ?? 'Could not load feed.'}
        </p>
        {feedHost && (
          <p style={{ margin: 0, fontSize: 'var(--text-label)', color: 'var(--color-text-dim)' }}>
            {feedHost}
          </p>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {items.length === 0 ? (
        <p style={{ margin: 0, fontSize: 'var(--text-detail)', color: 'var(--color-text-dim)' }}>
          No items in this feed.
        </p>
      ) : (
        <ul
          role="list"
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
          }}
        >
          {items.map((item, i) => (
            <li
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 'var(--space-2)',
                padding: 'var(--space-2)',
                background: 'var(--color-bg)',
                borderRadius: 'var(--radius-button)',
                border: '1px solid var(--color-border)',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--text-detail)',
                  fontWeight: 500,
                  color: 'var(--color-text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {/* Safe link: only https URLs without credentials — stored by fetch-feed.ts */}
                {item.link ? (
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--color-accent)', textDecoration: 'none' }}
                  >
                    {item.title}
                  </a>
                ) : (
                  item.title
                )}
              </span>
              <span
                style={{
                  fontSize: 'var(--text-delta)',
                  color: 'var(--color-text-dim)',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {formatRelativeTime(item.publishedAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {feedHost && (
        <p style={{ margin: 0, fontSize: 'var(--text-label)', color: 'var(--color-text-dim)' }}>
          {feedHost}
        </p>
      )}
    </div>
  );
}

export function ClockDetail({ tile, vm }: { tile: Tile; vm: GlanceViewModel }): React.ReactElement {
  const params = tile.source?.params as Record<string, unknown> | undefined;
  const tz = params?.tz as string | undefined ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const format = params?.format as string | undefined ?? '12h';

  // Live tick: update the displayed time every second while the sheet is open.
  const [liveTime, setLiveTime] = useState<string>(() => formatTime(tz, format));

  useEffect(() => {
    const tick = (): void => setLiveTime(formatTime(tz, format));
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [tz, format]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
        <span style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1 }}>
          {liveTime}
        </span>
        {vm.slots.delta && (
          <span style={{ fontSize: 'var(--text-detail)', color: 'var(--color-text-secondary)' }}>
            {vm.slots.delta}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', fontSize: 'var(--text-delta)', color: 'var(--color-text-dim)' }}>
        <span>Time zone: {tz}</span>
        <span>Format: {format === '24h' ? '24-hour' : '12-hour'}</span>
      </div>
    </div>
  );
}

export function SportsDetail({ tile, vm, onConfigure, analytics }: DetailContentProps): React.ReactElement {
  const params = tile.source?.params as Record<string, unknown> | undefined;
  const hasTeam = Boolean(params?.teamId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {hasTeam ? (
        <>
          {/* Score display */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
            <span style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1 }}>
              {vm.slots.primary || '—'}
            </span>
            {vm.slots.delta && (
              <span style={{ fontSize: 'var(--text-detail)', color: 'var(--color-text-secondary)' }}>
                {vm.slots.delta}
              </span>
            )}
          </div>

          {/* Team picker to change team (D-011) */}
          <SportsTeamPicker
            currentLeague={(params?.league as string | undefined) ?? 'nba'}
            currentTeamId={(params?.teamId as string | undefined) ?? ''}
            onSave={(league, teamId) => {
              onConfigure(tile.id, { league, teamId }, 'sports_team');
              // Emit tile_configured is done inside commitConfigure
              void analytics;
            }}
          />

          {/* D-010 attribution — legal requirement */}
          <div style={{
            fontSize: 'var(--text-timestamp)',
            color: 'var(--color-text-dim)',
            borderTop: '1px solid var(--color-border)',
            paddingTop: 'var(--space-2)',
          }}>
            <a
              href="https://www.thesportsdb.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--color-text-dim)', textDecoration: 'underline' }}
            >
              Data from TheSportsDB.com
            </a>
            {' '}· 2-min delayed scores
          </div>
        </>
      ) : (
        /* No team set: show the picker as the primary content (core-flows §4.3) */
        <SportsTeamPicker
          currentLeague={(params?.league as string | undefined) ?? 'nba'}
          currentTeamId=""
          onSave={(league, teamId) => {
            onConfigure(tile.id, { league, teamId }, 'sports_team');
          }}
          autoFocus
        />
      )}
    </div>
  );
}

/**
 * CalendarDetail — expanded view for calendar.peek tiles.
 *
 * Shows:
 * - Today's upcoming events (title + relative/absolute time, up to maxEvents)
 *   sourced from data._events (the CalendarEvent[] stashed alongside CalendarData)
 * - Disconnect button (clears local state; full revocation at Google account settings)
 * - Connect state when disconnected
 *
 * Privacy note (ADR-004): data._events comes from the client-side fetch;
 * it never transited our servers. The disconnect button clears local state only.
 */
export function CalendarDetail({
  tile,
  vm,
  data,
}: {
  tile: Tile;
  vm: GlanceViewModel;
  data?: Record<string, unknown>;
}): React.ReactElement {
  const [disconnecting, setDisconnecting] = useState(false);

  // Extract the event list from the stashed _events field (defensively)
  const events: CalendarEvent[] = Array.isArray(data?.['_events'])
    ? (data['_events'] as CalendarEvent[]).filter(
        (e): e is CalendarEvent =>
          typeof e === 'object' && e !== null && typeof (e as CalendarEvent).title === 'string',
      )
    : [];

  const params = tile.source?.params as { maxEvents?: number } | undefined;
  const maxEvents = params?.maxEvents ?? 3;
  const displayEvents = events.slice(0, maxEvents);

  // needsConnect: vm has primary 'Connect' when disconnected
  const needsConnect = vm.slots.primary === 'Connect';

  function handleDisconnect(): void {
    setDisconnecting(true);
    void import('../../adapters/calendar/google-client.js').then(({ disconnect }) => {
      disconnect();
      setDisconnecting(false);
      // The tile will show Connect on the next refresh cycle
    });
  }

  function formatEventTime(event: CalendarEvent): string {
    if (event.allDay) return 'All day';
    const d = new Date(event.startISO);
    if (isNaN(d.getTime())) return '';
    const diffMin = Math.floor((d.getTime() - Date.now()) / 60_000);
    if (diffMin <= 0) return 'Now';
    if (diffMin < 60) return `in ${diffMin}m`;
    const h = Math.floor(diffMin / 60);
    const m = diffMin % 60;
    const rel = m === 0 ? `in ${h}h` : `in ${h}h ${m}m`;
    const abs = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${rel} · ${abs}`;
  }

  if (needsConnect) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <p style={{ margin: 0, fontSize: 'var(--text-detail)', color: 'var(--color-text-secondary)' }}>
          Connect your Google Calendar to see upcoming events.
        </p>
        <button
          type="button"
          onClick={() => {
            void import('../../adapters/calendar/google-client.js').then(({ connectInteractive }) => {
              void connectInteractive();
            });
          }}
          style={{
            background: 'var(--color-accent)',
            border: 'none',
            borderRadius: 'var(--radius-button)',
            color: '#fff',
            fontSize: 'var(--text-detail)',
            fontWeight: 500,
            padding: '8px 20px',
            cursor: 'pointer',
            alignSelf: 'flex-start',
          }}
        >
          Connect Google Calendar
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {/* Upcoming events list */}
      {displayEvents.length === 0 ? (
        <p style={{ margin: 0, fontSize: 'var(--text-detail)', color: 'var(--color-text-dim)' }}>
          Clear — no upcoming events in your lookahead window.
        </p>
      ) : (
        <ul role="list" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {displayEvents.map((event, i) => (
            <li
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 'var(--space-2)',
                padding: 'var(--space-2)',
                background: 'var(--color-bg)',
                borderRadius: 'var(--radius-button)',
                border: '1px solid var(--color-border)',
              }}
            >
              <span style={{ fontSize: 'var(--text-detail)', fontWeight: 500, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {event.title}
              </span>
              <span style={{ fontSize: 'var(--text-delta)', color: 'var(--color-text-dim)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {formatEventTime(event)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Disconnect section */}
      <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        <button
          type="button"
          disabled={disconnecting}
          onClick={handleDisconnect}
          style={{
            background: 'none',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-button)',
            color: 'var(--color-text-secondary)',
            fontSize: 'var(--text-label)',
            padding: '6px 14px',
            cursor: disconnecting ? 'not-allowed' : 'pointer',
            alignSelf: 'flex-start',
            opacity: disconnecting ? 0.6 : 1,
          }}
        >
          {disconnecting ? 'Disconnecting…' : 'Disconnect'}
        </button>
        <p style={{ margin: 0, fontSize: 'var(--text-timestamp)', color: 'var(--color-text-dim)', lineHeight: 1.4 }}>
          Disconnects this browser session. To fully revoke access, visit your{' '}
          <a
            href="https://myaccount.google.com/permissions"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--color-accent)', textDecoration: 'none' }}
          >
            Google account permissions
          </a>.
        </p>
      </div>
    </div>
  );
}

export function DetailContent({ tile, vm, data, onConfigure, analytics }: DetailContentProps): React.ReactElement {
  switch (tile.type) {
    case 'weather.current':
      return <WeatherDetail vm={vm} {...(data ? { data } : {})} />;
    case 'markets.quote':
    case 'markets.realtime':
      return <MarketsDetail tile={tile} vm={vm} />;
    case 'news.headlines':
      return <NewsDetail tile={tile} vm={vm} />;
    case 'rss.feed':
      return <RssDetail tile={tile} vm={vm} {...(data ? { data } : {})} />;
    case 'sports.score':
      return <SportsDetail tile={tile} vm={vm} {...(data ? { data } : {})} onConfigure={onConfigure} analytics={analytics} />;
    case 'clock.local':
      return <ClockDetail tile={tile} vm={vm} />;
    case 'calendar.peek':
      return <CalendarDetail tile={tile} vm={vm} {...(data ? { data } : {})} />;
    case 'note.sticky':
      return <NoteDetail tile={tile} vm={vm} {...(data ? { data } : {})} onConfigure={onConfigure} analytics={analytics} />;
    case 'todo.list':
      return <TodoDetail tile={tile} vm={vm} {...(data ? { data } : {})} onConfigure={onConfigure} analytics={analytics} />;
    case 'countdown.event':
      return <CountdownDetail tile={tile} vm={vm} {...(data ? { data } : {})} onConfigure={onConfigure} analytics={analytics} />;
    default:
      return (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
          <span style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1 }}>
            {vm.slots.primary}
          </span>
          {vm.slots.delta && (
            <span style={{ fontSize: 'var(--text-detail)', color: 'var(--color-text-secondary)' }}>
              {vm.slots.delta}
            </span>
          )}
        </div>
      );
  }
}
